// @ts-strict
import { modelOptions, mongoose, PropType, post, prop, type Ref } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import type { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, type SchemaConstructorArgs } from "../schemaBase.js";
import { SensorReadingConfiguration } from "../sensorReadingConfiguration/sensorReadingConfiguration.js";
import { AreaOfInterestFieldsWithDefaults, AreaOfInterestSchemaTypes } from "../types/areaOfInterest.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import * as utilities from "./utilities.js";

const SCHEMA_KEY = "AreaOfInterest" as const;

@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "areaofinterests",
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<AreaOfInterest>>(...addSchemaKeyHook(SCHEMA_KEY))
export class AreaOfInterest extends SchemaBase<AreaOfInterest, AreaOfInterestFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({
        zodValidator: ObjectIdString,
        responseOnly: true,
    })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public name!: string;

    @validate({
        childClass: VertexInfo,
        childClassTransform: (object) => z.array(z.array(object)),
    })
    @prop({ type: () => [[VertexInfo]], required: true }, PropType.ARRAY)
    public polygons!: VertexInfo[][];

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: true, default: new Date() })
    public created: Date = new Date();

    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public deleted: boolean = false;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    @validate({
        childClass: SensorReadingConfiguration,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => SensorReadingConfiguration, required: false })
    public sensorReadingConfiguration?: SensorReadingConfiguration;

    static utilities = { ...utilities } as const;

    static dtoValidators: DtoValidatorTypes<AreaOfInterestSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<AreaOfInterest, AreaOfInterestFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

AreaOfInterest.dtoValidators = getZodSchemaFromClass(
    AreaOfInterest,
) as unknown as DtoValidatorTypes<AreaOfInterestSchemaTypes>;
